import { useCallback, useState } from 'react'

import type { ErrorResponse } from '~/apis'
import { updateProfileAPI } from '~/apis'
import { useAuthContext } from '~/hooks/useAuthContext'
import { useWrappedToast } from '~/hooks/useWrappedToast'
import { validateDisplayName } from '~/utils/validator'

type TFormMavisName = {
  name: string
}

type TCallback = () => void

const MESSAGE_UPDATE_SUCCESSFULLY = 'Profile name updated successfully'
const MESSAGE_ERROR_SAME_NAME = 'It is the same as your current name'

const initErrors = {
  name: '',
}

export const useAccountInfo = () => {
  const { user, updateContextProfile } = useAuthContext()
  const { showSuccessMessage } = useWrappedToast()

  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState(initErrors)

  const onChangeForm = () => {
    setErrors(initErrors)
  }

  const validateForm = useCallback(
    (form: TFormMavisName) => {
      const { name } = form

      const errName =
        name === user?.name
          ? MESSAGE_ERROR_SAME_NAME
          : validateDisplayName(name)

      return {
        isValid: !errName,
        errors: {
          name: errName,
        },
      }
    },
    [user?.name],
  )

  const handleUpdateProfile = async (name: string) => {
    if (isLoading) return

    setIsLoading(true)
    try {
      const data = await updateProfileAPI({ name })

      updateContextProfile({ name: data.name })
      showSuccessMessage(MESSAGE_UPDATE_SUCCESSFULLY)
    } catch (err) {
      const error = err as ErrorResponse
      setErrors({
        name: error.error_message,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmitChangeName = (event: React.SyntheticEvent, fn?: TCallback) => {
    event.preventDefault()

    const target = event.target as typeof event.target & {
      name: { value: string }
    }

    const name = target.name.value.trim()

    const { isValid, errors } = validateForm({ name })

    if (!isValid) {
      setErrors(errors)
      return
    }

    handleUpdateProfile(name)

    fn?.()
  }

  return {
    user,
    isLoading,
    errors,
    onChangeForm,
    onSubmitChangeName,
  }
}
